import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import styled from "styled-components/macro";
import * as paths from "../../../common/paths";
import Card from "../../Card";
import DataSourceList from "./DataSourceList";
import DataSourceArchiveList from "./DataSourceArchiveList";
import StyledLink from "../../StyledLink";
import Button from "../../Button";
import Modal from "../../Modal";
import UploadSource from "../../Forms/UploadSource";
import { toast } from "react-toastify";

// The card lays its actions out in a row. Stacked instead, so the upload action reads
// as its own thing under the archive link rather than crowding it.
const HeaderActions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.5rem;
`;

const DataSourceListWidget = ({ archive }) => {
  const history = useHistory();
  const [showUpload, setShowUpload] = useState(false);

  // Bumped when an upload creates a source, to pull the new row into the list. The
  // import-completed notification carries no data source id, so there is nothing to
  // navigate to — refetching the list is how the source appears.
  const [refreshKey, setRefreshKey] = useState(0);

  // Rendered into the container DataSourceList already mounts; a second one here would
  // show every toast twice. Upload is only offered in the non-archive branch, which is
  // exactly where that list is rendered.
  const importErrors = (message) => {
    toast.error(message, {
      position: "top-right",
      autoClose: 6000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    });
  };

  //Body Section of Widget
  function Body() {
    if (archive) return <DataSourceArchiveList />;
    return <DataSourceList key={refreshKey} />;
  }

  function Actions() {
    if (archive) return <StyledLink to={`/sources`}>View Active</StyledLink>;
    return (
      <HeaderActions>
        <StyledLink to={`/sources/archive`}>View Archive</StyledLink>
        {/* Uploading a one-off file starts here rather than on the connection holding
            uploaded files. Someone with a spreadsheet is thinking about the data they
            want to check, not about which connection it belongs to, and that connection
            sorts by name into a later page of the connections list. The connection's own
            Sources tab still offers the same control. */}
        <Button type="button" onClick={() => setShowUpload(true)}>
          Upload a File
        </Button>
      </HeaderActions>
    );
  }

  return (
    <>
      <Card
        title={"Sources"}
        titleDescription={
          archive ? "Archived Data Sources" : "Available Data Sources"
        }
        body={Body}
        actions={Actions}
      />
      {showUpload && (
        <Modal title={`Upload a File`} hide={() => setShowUpload(false)}>
          {/* No connection is passed: the server resolves the upload connection and
              returns which one it used, so this does not hold a copy of that id. */}
          <UploadSource
            onComplete={() => {
              setShowUpload(false);
              setRefreshKey((key) => key + 1);
            }}
            importErrors={importErrors}
            /* Uploading from here starts with nothing in view, so the source that was just
               created is what the user came for -- opening it saves them finding it in the
               list. Passed only from here: uploading from a connection's own page happens
               inside that connection, and navigating away would drop the user out of it. */
            onSourceCreated={(sourceId) =>
              history.push(paths.dataSource(sourceId))
            }
          />
        </Modal>
      )}
    </>
  );
};

export default DataSourceListWidget;
